/* eslint-disable max-len */
import { Theme } from '@material-ui/core';
import { Overrides } from '@material-ui/core/styles/overrides';
import {
  body2,
  button,
  captionAlt,
  fontRegular,
  overline,
  suisseBold,
  suisseLight,
  suisseMedium,
  suisseRegular,
  suisseSemiBold
} from 'assets/fonts';
import { BTCSPrimaryColor, spacingUnit } from 'assets/theme/constants';

const appBarHeight = 90;
const drawerWidth = 300;

export const overrides = (theme: Theme): Overrides => ({
  // register our @font-face values for each varient of Suisse Intl
  MuiCssBaseline: {
    '@global': {
      '@font-face': [suisseLight, suisseRegular, suisseMedium, suisseSemiBold, suisseBold],
    },
  },
  MuiContainer: {
    root: {
      paddingBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(1),
      },
    }
  },
  MuiBadge: {
    colorPrimary: {
      backgroundColor: BTCSPrimaryColor,
    },
  },
  MuiAppBar: {
    root: {
      height: appBarHeight,
      alignItems: 'flex',
      zIndex: theme.zIndex.drawer + 1,
      boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.07)',
      [theme.breakpoints.down('sm')]: {
        height: 70,
      },
    },
  },
  MuiGrid: {
    item: {
      ...body2,
      padding: `0px ${spacingUnit}px`,
    },
  },
  MuiBackdrop: {
    root: {
      backgroundColor: 'rgba(0, 0, 0, 0.35)',
    },
  },
  MuiFormHelperText: {
    root: {
      textTransform: 'none',
    },
  },
  MuiDivider: {
    light: {
      backgroundColor: 'rgba(243,243,243,1)',
    },
  },
  MuiStepIcon: {
    root: {
      color: theme.palette.grey[400],
    },
  },
  MuiButton: {
    root: {
      transition: 'none',
      borderRadius: spacingUnit * 4, // value from our BTCS-design principles
      padding: `${spacingUnit}px ${spacingUnit * 3}px`, // value from our BTCS-design principles
      textTransform: 'inherit', // normal case button,
      ...button,
    },
    contained: {
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none !important',
      },
      '&:active': {
        boxShadow: 'none !important',
      },
    },
    textPrimary: {
      // -1 because of the border
      // padding: (spacingUnit - 1) + "px " + (spacingUnit * 3 - 1) + "px", // value from our BTCS-design principles
      '&:hover': {
        backgroundColor: 'transparent',
        opacity: 0.6,
      },
    },
    textSecondary: {
      // -1 because of the border
      // padding: (spacingUnit - 1) + "px " + (spacingUnit * 3 - 1) + "px", // value from our BTCS-design principles
      '&:hover': {
        backgroundColor: 'transparent',
        opacity: 0.6,
      },
    },
    text: {
      // -1 because of the border
      // padding: (spacingUnit - 1) + "px " + (spacingUnit * 3 - 1) + "px", // value from our BTCS-design principles
      '&:hover': {
        backgroundColor: 'transparent',
        opacity: 0.6,
      },
    },
    outlined: {
      // -1 because of the border
      padding: `${spacingUnit - 1}px ${spacingUnit * 3 - 1}px`, // value from our BTCS-design principles
    },
  },
  MuiTab: {
    root: {
      minWidth: 0,
      textTransform: 'uppercase',
    },
    wrapper: {
      textTransform: 'uppercase',
    },
  },
  MuiSnackbar: {
    root: {

    },
  },
  MuiStepConnector: {
    vertical: {
      padding: 0,
    },
  },
  MuiCircularProgress: {
    root: {
      margin: `${spacingUnit * 4}px auto`,
      display: 'block',
    },
    circleIndeterminate: {
      strokeWidth: 5.5,
      r: 18,
    },
  },
  MuiTypography: {
    gutterBottom: {
      marginBottom: spacingUnit * 2,
    },
  },
  MuiStepLabel: {
    label: {
      fontSize: '0.8rem',
      fontWeight: 900,
    },
  },
  MuiAvatar: {
    colorDefault: {
      color: 'currentColor',
      backgroundColor: 'inherit',
    },
    root: {
      width: spacingUnit * 5,
      height: spacingUnit * 5,
    },
  },
  MuiFormGroup: {
    root: {
      marginTop: spacingUnit * 2,
      marginBottom: spacingUnit * 2,
    },
  },
  MuiTooltip: {
    popper: {
      // prevents interaction with the tooltip
      pointerEvents: 'none',
    },
  },
  MuiToolbar: {
    root: {
      height: appBarHeight,
    }
  },
  MuiBottomNavigation: {
    root: {
      backgroundColor: '#EEEEEE',
    },
  },

  /* CARDS */
  MuiCard: {
    root: {
      overflow: 'visible',
      borderRadius: theme.spacing(1),
    },
  },
  MuiCardHeader: {
    avatar: {
      '& svg': {
        display: 'block',
      },
    },
    title: {
      fontWeight: 'bold',
      fontSize: theme.typography.h6.fontSize,
    },
  },
  MuiCardContent: {
    root: {
      '&:last-child': {
        paddingBottom: spacingUnit * 2,
      },
    },
  },
  MuiCardActions: {
    root: {
      padding: theme.spacing(4),
    },
  },
  MuiPaper: {
    rounded: {
      borderRadius: theme.spacing(1),
    },
    root: {
      overflow: 'hidden',
    },
  },
  MuiDialog: {
    container: {
      padding: theme.spacing(4),
      [theme.breakpoints.down('sm')]: {
        padding: 0,
      },
    },
    paperScrollPaper: {
      maxHeight: '100%',
    },
    paperFullWidth: {
      width: '100%',
    },
    paper: {
      margin: 0,
    },
  },

  /* TABLE */
  MuiTable: {
    root: {
      // so we can place items within in absolute position.
      // see TableRowActions component
      // position: 'relative',
      // tableLayout: 'fixed',
      // marginTop: theme.spacing(2),
      // marginBottom: theme.spacing(2),
    },
  },
  MuiTableHead: {
    root: {
      // TODO: figure out how to pull in active palette colors :thinking: emoji
      // backgroundColor: '#F9F9F9',
      // creates a small 8px between the table header and first table row
      '&:after': {
        content: `${'""'}`,
        display: 'table-row',
        height: theme.spacing(2),
        background: '#ffffff',
      },
    },
  },
  MuiTablePagination: {
    select: {
      ...captionAlt,
      textTransform: 'inherit',
      paddingRight: spacingUnit * 3,
    },
    caption: {
      ...captionAlt,
      textTransform: 'inherit',
    },
    selectIcon: {
      top: 3,
    },
  },
  MuiTableCell: {
    root: {
      // paddingTop: spacingUnit * 3,
      // paddingBottom: spacingUnit * 3,
      borderBottom: 'solid 1px #f3f3f3',
      '&[hidden]': {
        display: 'none',
      },
    },
    head: {
      ...overline,
      top: 0,
      position: 'sticky',
      color: theme.palette.text.hint,
      borderBottom: 'none',
      paddingTop: spacingUnit * 2,
      paddingBottom: spacingUnit * 2,
      backgroundColor: '#F9F9F9',
      '&:first-child': {
        borderBottomLeftRadius: spacingUnit,
        borderTopLeftRadius: spacingUnit,
      },
      '&:last-child': {
        borderBottomRightRadius: spacingUnit,
        borderTopRightRadius: spacingUnit,
      },
    },
  },
  MuiTableRow: {
    root: {
      borderBottom: 'solid 1px #f3f3f3',
      // pseudo first and last child for reach row to accomodate row underine going to edge of card
      '&:before, &:after': {
        content: `${'""'}`,
        display: 'table-cell',
        width: theme.spacing(2),
        borderBottom: 'solid 1px #f3f3f3',
      },
      '&:last-child, &:last-child:before, &:last-child:after': {
        borderBottom: 'none',
      },
    },
    head: {
      borderBottom: 'none',
      '&:before, &:after': {
        backgroundColor: theme.palette.background.paper,
        borderBottom: 'none',
      },
      '&:hover': {
        backgroundColor: 'none',
      },
    },
    hover: {
      backgroundColor: 'none',
      boxShadow: 'none', // shadows[3],
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: `${theme.palette.background.default} !important`, // NOTE: (Jesse) not ideal to use important, but can't find better solution right now
      },
    },
  },

  /* FORMS */
  MuiFormControl: {
    root: {
      '&[hidden]': {
        display: 'none',
      },
    },
  },
  MuiCheckbox: {
    root: {
      '&$checked': {
        color: '#CF0A2C" !important',
      },
    },
  },
  MuiMenuItem: {
    root: {
      paddingBottom: spacingUnit * 2,
      paddingTop: spacingUnit * 2,
      fontSize: '1rem',
    },
  },
  MuiFormLabel: {
    root: {
      fontSize: '1rem',
      '&$focused': {
        color: 'default',
      },
      '&$disabled': {
        color: theme.palette.grey[400],
      },
    },
  },
  MuiFilledInput: {
    root: {
      borderRadius: 4,
    },
  },
  MuiInputBase: {
    root: {
      '&$disabled': {
        color: theme.palette.grey[400],
      },
    },
  },
  MuiInputLabel: {
    root: {
      letterSpacing: 0,
      '&$disabled': {
        color: theme.palette.grey[400],
      },
    },
    shrink: {
      fontSize: '.75rem',
      transform: 'translate(0, 1.5px)',
      transformOrigin: 'top left',
    },
  },
  MuiInput: {
    root: {
      fontSize: '1rem',
    },
  },
  MuiRadio: {
    root: {
      '&$checked': {
        color: '#CF0A2C" !important',
      },
    },
  },
  MuiIcon: {
    root: {
      lineHeight: 0,
    },
  },
  MuiSvgIcon: {
    root: {
      lineHeight: 0,
    },
  },
  MuiIconButton: {
    root: {
      // align with logo color
      color: 'inherit',
      opacity: 0.9,
    },
  },
  MuiListItemIcon: {
    root: {
      // align with logo color
      color: 'inherit',
      opacity: 0.9,
      // minHeight: 44, // from Payment ..
    },
  },
  MuiListItem: {
    root: {
      fontSize: theme.typography.body2.fontSize,
      minHeight: 44,
      fontFamily: fontRegular,
    },
    gutters: {
      paddingTop: spacingUnit * 2,
      paddingBottom: spacingUnit * 2,
      paddingRight: spacingUnit * 4,
      paddingLeft: spacingUnit * 4,
    },
    button: {
      transition: 'none',
      '&:hover': {
        backgroundColor: '#fbfbfb',
      },
    },
  },
  MuiDrawer: {
    paper: {
      paddingTop: 90,
      width: drawerWidth
    },
    paperAnchorLeft: {
      borderRight: 'none',
      boxShadow: 'none',
    },
    paperAnchorDockedLeft: {
      borderRight: 'none',
      boxShadow: 'none',
    },
  },
  MuiExpansionPanel: {
    rounded: {
      '&:first-child': {
        borderTopLeftRadius: spacingUnit * 0.5,
        borderTopRightRadius: spacingUnit * 0.5,
      },
      '&:last-child': {
        borderBottomLeftRadius: spacingUnit * 0.5,
        borderBottomRightRadius: spacingUnit * 0.5,
      },
    },
  },
  MuiExpansionPanelSummary: {
    root: {
      padding: spacingUnit * 2,
      minHeight: `${spacingUnit * 6}px !important`,
    },
    expandIcon: {
      padding: `0 ${spacingUnit * 2}px`,
    },
    content: {
      margin: '0 !important',
      fontWeight: 'bold',
      fontSize: '1rem',
      alignItems: 'center',
      // color: dna.typography.body2.color,
      // fontFamily: dna.typography.fontFamily,
      '& svg': {
        display: 'block',
        marginRight: spacingUnit * 2,
      },
    },
  },
});
